import type { StudioDesktopBridge, StudioDesktopCapabilities } from './capabilities'
import { WEB_CAPABILITIES } from './capabilities'

/**
 * Returns the desktop bridge when running inside the Electron shell, otherwise
 * `null`. Safe to call during SSR or in tests: it never throws if `window` is
 * undefined.
 */
export function getStudioDesktopBridge(): StudioDesktopBridge | null {
  const host = globalThis as { studioDesktop?: StudioDesktopBridge }
  return host.studioDesktop ?? null
}

export function isStudioDesktop(): boolean {
  return getStudioDesktopBridge() !== null
}

/**
 * Whether the desktop shell hides the native titlebar and paints the Studio
 * header as a custom draggable titlebar (macOS only). Reads the synchronous
 * preview so the renderer can choose its header chrome on first paint. Returns
 * `false` on the web and on platforms that keep the native frame (Windows/Linux).
 */
export function studioDesktopUsesCustomTitlebar(): boolean {
  return getStudioDesktopBridge()?.capabilities.usesCustomTitlebar ?? false
}

/**
 * Synchronous preview of the supported local tool routes, available on first
 * paint without awaiting `getCapabilities()`. Empty on the web. Used to render
 * the "Local models" navigation group optimistically.
 */
export function getStudioDesktopLocalToolRoutes(): readonly string[] {
  return getStudioDesktopBridge()?.capabilities.localToolRoutes ?? []
}

/**
 * Resolves the host capabilities, falling back to web defaults whenever the
 * bridge is missing or fails to respond. Always resolves; never rejects.
 */
export async function getStudioDesktopCapabilities(): Promise<StudioDesktopCapabilities> {
  const bridge = getStudioDesktopBridge()
  if (!bridge) {
    return WEB_CAPABILITIES
  }
  try {
    return await bridge.getCapabilities()
  } catch {
    return WEB_CAPABILITIES
  }
}
